import { Alert, Box, CircularProgress, Divider, Stack } from "@mui/material";
import { ReactNode, useContext, useEffect, useRef } from "react";
import { GetPortraitResponse } from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { usePortraitsTranslator } from "../i18n/translator";
import { CardStep } from "./CardStep";
import { ChristmasImageStep } from "./ChristmasImageStep";
import { PhotoStep } from "./PhotoStep";
import { Stage } from "./portraitStages";
import { PortraitSubjectContext } from "./portraitSubject";
import { usePortrait } from "./usePortrait";
import { usePortraitFlow } from "./usePortraitFlow";

const SUBTITLES = {
  card: "subtitleCard",
  photo: "subtitlePhoto",
  christmasImage: "subtitleChristmasImage",
} as const satisfies Record<Stage, string>;

const STEPS = {
  card: CardStep,
  photo: PhotoStep,
  christmasImage: ChristmasImageStep,
} satisfies Record<Stage, unknown>;

type FlowProps = {
  name: string | undefined;
  renderHeading?: (subtitle: string) => ReactNode;
  renderFlag: (portrait: GetPortraitResponse) => ReactNode;
  /** Admins only look at first, without anything being generated for them */
  generateOnArrival: boolean;
};

const LoadedPortraitFlow = ({
  portrait,
  name,
  renderHeading,
  renderFlag,
  generateOnArrival,
}: FlowProps & { portrait: GetPortraitResponse }) => {
  const translator = usePortraitsTranslator();
  const flow = usePortraitFlow(portrait, { generateOnArrival });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shownStage = useRef(flow.stage);

  // Each step replaces the previous one, so the focus would otherwise be lost
  useEffect(() => {
    if (shownStage.current !== flow.stage) {
      shownStage.current = flow.stage;
      headingRef.current?.focus();
    }
  }, [flow.stage]);

  const Step = STEPS[flow.stage];
  const flag = renderFlag(portrait);

  return (
    <Stack spacing={3} alignItems="center">
      {renderHeading && (
        <>
          <Box width="100%">
            {renderHeading(translator(SUBTITLES[flow.stage]))}
          </Box>
          <Divider flexItem />
        </>
      )}
      {flag && (
        <Box width="100%" maxWidth={480}>
          {flag}
        </Box>
      )}
      <Step
        portrait={portrait}
        name={name}
        flow={flow}
        headingRef={headingRef}
      />
    </Stack>
  );
};

export const PortraitFlow = (props: FlowProps) => {
  const translator = usePortraitsTranslator();
  const { data: portrait, isError } = usePortrait();
  const { apiPath } = useContext(PortraitSubjectContext);

  if (isError) {
    return (
      <Alert severity="error">{translator("unknownErrorTryAgainLater")}</Alert>
    );
  }

  if (!portrait) {
    return <CircularProgress aria-busy />;
  }

  // Starts over for each colleague an admin opens
  return <LoadedPortraitFlow key={apiPath} portrait={portrait} {...props} />;
};
